import { useEffect, useRef } from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { SIGNUP_ROUTES } from '../utils/signupOrderAb';
import { logUxEvent } from '../telemetry/uxLogger';
import { peekPendingAccessBlockedMessage } from '../utils/emailStepRegistration';

/**
 * Cadastro concluído não volta a ser percorrido pelo "voltar" do sistema.
 *
 * Auditoria UX 10/10/2026: as telas do cadastro ficam no histórico, então
 * quem aperta "voltar" na triagem ou no chat desce /termos → /telefone →
 * /cpf → /email → /consulta, uma tela por toque. Ninguém que entrou nessa
 * cascata voltou a concluir a triagem. Durante o cadastro o "voltar" segue
 * passo a passo; depois do aceite dos Termos, um POP que caia numa tela do
 * cadastro salta para a entrada anterior a ele.
 */

const BASE_KEY = 'signupHistoryBase';
const DONE_KEY = 'signupCompletedFor';
const SIGNUP_PATHS = new Set(Object.values(SIGNUP_ROUTES));

export const isSignupPath = (pathname) => SIGNUP_PATHS.has(String(pathname || '').replace(/\/+$/, '') || '/');

const currentPatientId = () => {
  try {
    return sessionStorage.getItem('patientId') || localStorage.getItem('patientId') || '';
  } catch {
    return '';
  }
};

const historyIdx = () => {
  try {
    const idx = window.history.state && window.history.state.idx;
    return typeof idx === 'number' ? idx : null;
  } catch {
    return null;
  }
};

/** Chamado quando o aceite leva para fora do cadastro (triagem ou chat). */
export function markSignupHistoryCompleted() {
  try {
    const pid = currentPatientId();
    if (pid) sessionStorage.setItem(DONE_KEY, pid);
  } catch {}
}

function isCompletedForCurrentPatient() {
  try {
    const pid = currentPatientId();
    return !!pid && sessionStorage.getItem(DONE_KEY) === pid;
  } catch {
    return false;
  }
}

export default function SignupHistoryGuard() {
  const location = useLocation();
  const navType = useNavigationType();
  const navigate = useNavigate();
  const prevPathRef = useRef(null);

  // O bloqueio de acesso chega enquanto o "Continuar como" já avançou. Quem
  // está montado o tempo todo devolve o paciente para o aviso, mesmo que
  // uma navegação atrasada tenha coberto a primeira tentativa.
  useEffect(() => {
    const forceBlockedEmail = () => {
      if (!peekPendingAccessBlockedMessage()) return;
      const path = String(window.location.pathname || '').replace(/\/+$/, '') || '/';
      if (path === '/email') return;
      if (!isSignupPath(path) && path !== '/cupom-primeira') return;
      navigate('/email', { replace: true });
    };
    forceBlockedEmail();
    window.addEventListener('cm:signup-access-blocked', forceBlockedEmail);
    return () => window.removeEventListener('cm:signup-access-blocked', forceBlockedEmail);
  }, [location.pathname, navigate]);

  useEffect(() => {
    const path = location.pathname;
    const prevPath = prevPathRef.current;
    prevPathRef.current = path;
    if (!isSignupPath(path)) return undefined;

    // A carga inicial da página também chega como POP; recarregar uma tela
    // do cadastro não pode ser tratado como "voltar". Quem abre uma tela do
    // cadastro com a página recém-carregada está cadastrando: a marca de
    // concluído de um cadastro anterior na mesma aba não vale mais.
    if (prevPath === null) {
      try {
        const cur = historyIdx();
        const raw = sessionStorage.getItem(BASE_KEY);
        const base = raw === null || raw === '' ? null : Number(raw);
        if (base === null || !Number.isFinite(base) || (typeof cur === 'number' && base > cur)) {
          sessionStorage.setItem(BASE_KEY, String(cur ?? ''));
        }
        sessionStorage.removeItem(DONE_KEY);
      } catch {}
      return undefined;
    }

    if (navType !== 'POP') {
      if (prevPath !== null && isSignupPath(prevPath)) return undefined;
      try {
        sessionStorage.setItem(BASE_KEY, String(historyIdx() ?? ''));
        sessionStorage.removeItem(DONE_KEY);
      } catch {}
      return undefined;
    }

    if (!isCompletedForCurrentPatient()) return undefined;

    let base = null;
    try {
      const raw = sessionStorage.getItem(BASE_KEY);
      base = raw === null || raw === '' ? null : Number(raw);
    } catch {}
    const cur = historyIdx();
    const steps = typeof base === 'number' && Number.isFinite(base) && typeof cur === 'number' && cur >= base
      ? cur - base + 1
      : null;
    try { logUxEvent('signup_history_skipped', { from: path, steps }); } catch {}

    if (!steps) {
      navigate('/', { replace: true });
      return undefined;
    }
    const startKey = location.key;
    window.history.go(-steps);
    // Sem entrada anterior ao cadastro (aba aberta direto em /consulta), o
    // go() não sai do lugar: cai na Home para o paciente não ficar no cadastro.
    const fallback = setTimeout(() => {
      try {
        if (window.location.pathname === path && (window.history.state || {}).key === startKey) {
          navigate('/', { replace: true });
        }
      } catch {}
    }, 600);
    return () => clearTimeout(fallback);
  }, [location.key, location.pathname, navType, navigate]);

  return null;
}
